import { useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid2,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  useTheme,
  Switch,
  FormControlLabel
} from "@mui/material"
import {
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
  Save as SaveIcon,
  Settings as SettingsIcon
} from "@mui/icons-material"
import { useForm, Controller } from "react-hook-form"
import {
  useCreatePlatformFeeConfigMutation,
  useUpdatePlatformFeeConfigMutation,
  useGetPlatformFeeConfigQuery,
  CreatePlatformFeeConfigDto,
  PlatformFeeType,
  Status
} from "src/graphql/quark/quark.type"
import OutlinedInput from "src/shared/components/outlined-input"
import InputAutocomplete from "src/shared/components/input-autocomplete"
import { PLATFORM_FEE_ORDER_TYPE_OPTIONS, PLATFORM_FEE_TYPE_OPTIONS } from "../../constants"
import { Conditional } from "src/shared/components"
import { toast } from "react-toastify"
import { useSearchParams } from "react-router-dom"
import { useTypedSelector } from "src/shared/store/store"
import { PlatformFeeOrderType } from "../../types"

type PlatformFeeFormData = CreatePlatformFeeConfigDto

const initialValues: PlatformFeeFormData = {
  locationId: "",
  name: "",
  orderType: PlatformFeeOrderType.Delivery,
  platformFeeType: PlatformFeeType.Flat,
  platformFeeValue: 0,
  platformFeeTaxRate: 0,
  platformFeeTaxable: false,
  platformFeeAccountCode: undefined
}

const CreateUpdatePlatformFeeModal = () => {
  const theme = useTheme()
  const currentWhLoc = useTypedSelector((state) => state.authentication.currentWhLoc)
  const [searchParams, setSearchParams] = useSearchParams()
  const platformFeeId = searchParams.get("id")
  const formMode = searchParams.get("mode")
  const isOpen = !!formMode

  const { data, loading: isGetPlatformFeeLoading } = useGetPlatformFeeConfigQuery({
    variables: { getPlatformFeeConfigId: platformFeeId as string },
    skip: !platformFeeId
  })
  const platformFee = data?.getPlatformFeeConfig

  const { control, handleSubmit, reset } = useForm<PlatformFeeFormData>({
    defaultValues: initialValues
  })

  const [createPlatformFee, { loading: createLoading }] = useCreatePlatformFeeConfigMutation()
  const [updatePlatformFee, { loading: updateLoading }] = useUpdatePlatformFeeConfigMutation()

  useEffect(() => {
    if (platformFee && formMode === "edit") {
      reset({
        name: platformFee.name || "",
        orderType: platformFee.orderType || PlatformFeeOrderType.Delivery,
        platformFeeType: platformFee.platformFeeType || PlatformFeeType.Flat,
        platformFeeValue: platformFee.platformFeeValue || 0,
        platformFeeTaxRate: platformFee.platformFeeTaxRate || 0,
        platformFeeTaxable: platformFee.platformFeeTaxable || false,
        platformFeeAccountCode: platformFee.platformFeeAccountCode || "",
        status: platformFee.status || Status.Active,
        isRemovable: platformFee.isRemovable || false
      })
    } else if (formMode === "create") {
      reset({
        ...initialValues,
        locationId: currentWhLoc?.id as string
      })
    }
  }, [platformFee, formMode, reset])

  const onSubmit = async (data: PlatformFeeFormData) => {
    try {
      const platformFeeInput = {
        locationId: data?.locationId,
        name: data.name,
        orderType: data.orderType,
        platformFeeType: data.platformFeeType,
        platformFeeValue: Number(data.platformFeeValue),
        platformFeeTaxRate: Number(data.platformFeeTaxRate),
        platformFeeTaxable: data.platformFeeTaxable,
        platformFeeAccountCode: data.platformFeeAccountCode,
        status: data.status,
        isRemovable: data.isRemovable
      }

      if (formMode === "create") {
        const resp = await createPlatformFee({
          variables: { createPlatformFeeConfigInput: platformFeeInput },
          refetchQueries: ["GetPlatformFeeConfigs"]
        })
        resp.data?.createPlatformFeeConfig.id
          ? toast.success("Platform fee created successfully")
          : toast.error("Failed to create platform fee")
      } else {
        const resp = await updatePlatformFee({
          variables: {
            updatePlatformFeeConfigInput: {
              ...platformFeeInput,
              id: platformFee?.id as string
            }
          },
          refetchQueries: ["GetPlatformFeeConfigs"]
        })
        resp.data?.updatePlatformFeeConfig
          ? toast.success("Platform fee updated successfully")
          : toast.error("Failed to update platform fee")
      }
      handleClose()
    } catch (error) {
      console.error("Error saving platform fee:", error)
      toast.error("An error occurred while saving the platform fee")
    }
  }

  const handleClose = () => {
    reset(initialValues)
    setSearchParams({})
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "600px"
        }
      }}
    >
      <Conditional condition={!isGetPlatformFeeLoading} elseBlock={<div>Loading...</div>}>
        <DialogTitle
          sx={{
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            color: theme.palette.primary.contrastText,
            py: 3,
            px: 4,
            position: "relative"
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {formMode === "create" ? "Create Platform Fee" : "Edit Platform Fee"}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {formMode === "create" ? "Configure platform fees for your business" : "Update platform fee"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: theme.palette.primary.contrastText
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ background: theme.palette.background.default }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid2 container spacing={3} sx={{ py: 4 }}>
              {/* Basic Information Section */}
              <Grid2 size={12}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <SettingsIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Basic Information
                  </Typography>
                </Box>

                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12 }}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: "Platform fee name is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <OutlinedInput
                          {...field}
                          fullWidth
                          label="Platform Fee Name"
                          placeholder="Enter platform fee name"
                          error={!!error}
                          required
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="orderType"
                      control={control}
                      rules={{ required: "Order type is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <InputAutocomplete
                          onChange={(option: any) => {
                            field.onChange(option.value)
                          }}
                          label="Order Type"
                          options={PLATFORM_FEE_ORDER_TYPE_OPTIONS}
                          value={PLATFORM_FEE_ORDER_TYPE_OPTIONS.find((option) => option.value === field.value)}
                          disableClearable={true}
                          required
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="platformFeeType"
                      control={control}
                      rules={{ required: "Fee type is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <InputAutocomplete
                          onChange={(option: any) => {
                            field.onChange(option.value)
                          }}
                          label="Fee Type"
                          options={PLATFORM_FEE_TYPE_OPTIONS}
                          value={PLATFORM_FEE_TYPE_OPTIONS.find((option) => option.value === field.value)}
                          disableClearable={true}
                          required
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>

              {/* Fee Configuration Section */}
              <Grid2 size={12}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <MoneyIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Fee Configuration
                  </Typography>
                </Box>

                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="platformFeeValue"
                      control={control}
                      rules={{
                        required: "Fee value is required",
                        min: { value: 0, message: "Fee value must be positive" }
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <OutlinedInput
                          {...field}
                          type="number"
                          fullWidth
                          label="Fee Value"
                          placeholder="Enter fee value"
                          error={!!error}
                          required
                          helperText={error?.message}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="platformFeeTaxRate"
                      control={control}
                      rules={{
                        required: "Tax rate is required",
                        min: { value: 0, message: "Tax rate must be positive" },
                        max: { value: 100, message: "Tax rate cannot exceed 100%" }
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <OutlinedInput
                          {...field}
                          type="number"
                          fullWidth
                          label="Tax Rate (%)"
                          placeholder="Enter tax rate"
                          error={!!error}
                          required
                          helperText={error?.message}
                          inputProps={{ min: 0, max: 100, step: 0.01 }}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="platformFeeTaxable"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Taxable"
                        />
                      )}
                    />
                    <Controller
                      name="isRemovable"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value || false}
                              onChange={(e) => field.onChange(e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Removable"
                        />
                      )}
                    />
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value ? field.value === Status.Active : false}
                              onChange={(e) => field.onChange(e.target.checked ? Status.Active : Status.Inactive)}
                              color="primary"
                            />
                          }
                          label="Status"
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="platformFeeAccountCode"
                      control={control}
                      // rules={{ required: "Account code is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <OutlinedInput
                          {...field}
                          fullWidth
                          label="Account Code"
                          placeholder="Enter account code"
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>

              {/* Current Selection Display */}
              {/* <Grid2 size={12}>
                <Fade in={isDirty}>
                  <Alert
                    severity="info"
                    sx={{
                      borderRadius: 2,
                      backgroundColor:
                        theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
                      color: theme.palette.text.primary,
                      "& .MuiAlert-message": { width: "100%" }
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" fontWeight="bold">
                        Current Configuration:
                      </Typography>
                      <Chip
                        icon={getOrderTypeIcon(watchedOrderType)}
                        label={PLATFORM_FEE_ORDER_TYPE_OPTIONS.find((opt) => opt.value === watchedOrderType)?.label}
                        color={getOrderTypeColor(watchedOrderType) as any}
                        size="small"
                      />
                      <Chip
                        icon={getFeeTypeIcon(watchedFeeType)}
                        label={PLATFORM_FEE_TYPE_OPTIONS.find((opt) => opt.value === watchedFeeType)?.label}
                        color={getFeeTypeColor(watchedFeeType) as any}
                        size="small"
                      />
                      <Chip
                        label={watchedTaxable ? "Taxable" : "Non-Taxable"}
                        color={watchedTaxable ? "success" : "default"}
                        size="small"
                      />
                    </Stack>
                  </Alert>
                </Fade>
              </Grid2> */}
            </Grid2>
          </form>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            p: 3,
            background: theme.palette.background.default
          }}
        >
          <Button onClick={handleClose} variant="outlined" size="large" sx={{ minWidth: 120 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            size="large"
            disabled={createLoading || updateLoading}
            startIcon={createLoading || updateLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{
              minWidth: 180,
              background: theme.palette.primary.main,
              "&:hover": {
                background: theme.palette.primary.dark
              }
            }}
          >
            {createLoading || updateLoading
              ? "Saving..."
              : formMode === "create"
                ? "Create Platform Fee"
                : "Update Platform Fee"}
          </Button>
        </DialogActions>
      </Conditional>
    </Dialog>
  )
}

export default CreateUpdatePlatformFeeModal
